import { Menu } from '@base-ui/react/menu';
import { css } from 'aphrodite';
import * as React from 'react';
import { BulletIcon } from '../../components/svgs/icons/BulletIcon';
import { DropdownPortalContainerContext } from '../DropdownPortalContainerContext';
import { CheckIcon } from '../../components/svgs/icons/CheckIcon';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { MoreIcon } from '../../components/svgs/icons/MoreIcon';
import { brandSecondary, navigation } from '../../styles/colors';
import { type DedupeGuard, dedupeMenuItemClick } from './dedupeMenuItemClick';
import { s } from './styles';

/**
 * Api docs: https://base-ui.com/react/components/menu
 */
const Dropdown = Menu.Root;

const DropdownGroup = Menu.Group;
DropdownGroup.displayName = 'DropdownGroup';

const DropdownRadioGroup = Menu.RadioGroup;
DropdownRadioGroup.displayName = 'DropdownRadioGroup';

const DropdownTrigger = Menu.Trigger;

const DropdownSubMenu = Menu.SubmenuRoot;

const DropdownSelectTrigger = React.forwardRef<
	HTMLButtonElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Trigger>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.Trigger
		ref={ref}
		data-slot='dropdown-menu-select-trigger'
		className={css(s.selectTrigger, ...styles)}
		{...props}
	>
		<span>{children}</span> <ChevronDown className={css(s.triggerChevron)} />
	</Menu.Trigger>
));
DropdownSelectTrigger.displayName = 'DropdownSelectTrigger';

type DropdownEllipsisTriggerProps = AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Trigger>> & {
	/**
	 * Override the dots' fill color. Defaults to the gray `navigation` token.
	 * Pass `'currentColor'` to inherit the surrounding text color (useful when
	 * the trigger sits on a colored bar whose text color already toggles per
	 * state — see `SectionHeader` in QuestionsWithSections).
	 */
	fillColor?: string;
};

const DropdownEllipsisTrigger = React.forwardRef<HTMLButtonElement, DropdownEllipsisTriggerProps>(
	({ styles = [], fillColor, ...props }, ref) => (
		<Menu.Trigger ref={ref} className={css(s.ellipsisTrigger, ...styles)} {...props}>
			<MoreIcon fillColor={fillColor ?? navigation} />
		</Menu.Trigger>
	)
);
DropdownEllipsisTrigger.displayName = 'DropdownEllipsisTrigger';

interface DropdownContentProps extends React.ComponentPropsWithoutRef<typeof Menu.Popup> {
	align?: Menu.Positioner.Props['align'];
	sideOffset?: Menu.Positioner.Props['sideOffset'];
}

const DropdownContent = React.forwardRef<HTMLDivElement, AphroditeStyled<DropdownContentProps>>(
	({ styles = [], align = 'center', sideOffset = 4, ...props }, ref) => {
		const portalContainerRef = React.useContext(DropdownPortalContainerContext);
		return (
			<Menu.Portal container={portalContainerRef ?? undefined}>
				<Menu.Positioner sideOffset={sideOffset} align={align} className={css(s.dropdownPositioner)}>
					<Menu.Popup ref={ref} className={css(s.dropdownPopup, ...styles)} {...props} />
				</Menu.Positioner>
			</Menu.Portal>
		);
	}
);
DropdownContent.displayName = 'DropdownContent';

const DropdownItem = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Item>>
>(({ styles = [], onClick, ...props }, ref) => {
	// See `dedupeMenuItemClick` for the base-ui mouseUp→click() workaround. The
	// guard lives in a ref so it survives the re-render that can commit between
	// base-ui's real click and its same-tick synthetic click() — `onClick` is
	// usually a fresh inline arrow, so without a stable guard `useMemo` would
	// rebuild the wrapper and reset the de-dupe, letting the duplicate through
	// (a duplicate→delete→duplicate double-insert).
	const dedupeGuard = React.useRef<DedupeGuard>({ firedThisTick: false });
	const handleClick = React.useMemo(() => dedupeMenuItemClick(onClick, dedupeGuard.current), [onClick]);
	return <Menu.Item ref={ref} className={css(s.menuItem, ...styles)} {...props} onClick={handleClick} />;
});
DropdownItem.displayName = 'DropdownItem';

const DropdownSeparator = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Separator>>
>(({ styles = [], ...props }, ref) => <Menu.Separator ref={ref} className={css(s.separator, ...styles)} {...props} />);
DropdownSeparator.displayName = 'DropdownSeparator';

const DropdownGroupLabel = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.GroupLabel>>
>(({ styles = [], ...props }, ref) => (
	<Menu.GroupLabel ref={ref} className={css(s.groupLabel, ...styles)} {...props} />
));
DropdownGroupLabel.displayName = 'DropdownGroupLabel';

const DropdownCheckboxItem = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.CheckboxItem>>
>(({ styles = [], children, onCheckedChange, ...props }, ref) => {
	// Same base-ui mouseUp→click() duplicate as `DropdownItem`. Two toggles in one tick cancel out, so
	// without this the item looks completely unresponsive. See `dedupeMenuItemClick` for why the guard is a ref.
	const dedupeGuard = React.useRef<DedupeGuard>({ firedThisTick: false });
	const handleCheckedChange = React.useMemo(
		() => dedupeMenuItemClick(onCheckedChange, dedupeGuard.current),
		[onCheckedChange]
	);
	return (
		<Menu.CheckboxItem
			ref={ref}
			className={css(s.checkboxItem, ...styles)}
			{...props}
			onCheckedChange={handleCheckedChange}
		>
			<div className={css(s.checkBoxItemCheckWrapper)}>
				<Menu.CheckboxItemIndicator className={css(s.checkBoxItemCheckIndicator)}>
					<CheckIcon className={css(s.checkBoxItemCheck)} />
				</Menu.CheckboxItemIndicator>
			</div>
			<span>{children}</span>
		</Menu.CheckboxItem>
	);
});
DropdownCheckboxItem.displayName = 'DropdownCheckboxItem';

const DropdownRadioItem = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.RadioItem>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.RadioItem ref={ref} className={css(s.radioItem, ...styles)} {...props}>
		<div className={css(s.radioItemCheckWrapper)}>
			<Menu.RadioItemIndicator className={css(s.radioItemCheckIndicator)}>
				<BulletIcon fillColor={brandSecondary} className={css(s.radioItemCheck)} />
			</Menu.RadioItemIndicator>
		</div>
		<span>{children}</span>
	</Menu.RadioItem>
));
DropdownRadioItem.displayName = 'DropdownRadioItem';

const DropdownSubTrigger = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.SubmenuTrigger>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.SubmenuTrigger ref={ref} className={css(s.subTrigger, ...styles)} {...props}>
		{children}
		<ChevronRight className={css(s.subTriggerChevron)} />
	</Menu.SubmenuTrigger>
));
DropdownSubTrigger.displayName = 'DropdownSubTrigger';

export {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownGroup,
	DropdownGroupLabel,
	DropdownItem,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownSelectTrigger,
	DropdownSeparator,
	DropdownSubMenu,
	DropdownSubTrigger,
	DropdownTrigger,
};
